export type TrackEditReferenceCoordinate = [number, number]

export type TrackEditReferenceDiffSegment = {
  coordinates: TrackEditReferenceCoordinate[]
  // Distance from the original track's start to this segment's first coordinate, so
  // hover popups can report positions along the original track.
  startDistanceM: number
}

const METERS_PER_DEGREE_LAT = 111_320
const REFERENCE_DIFF_TOLERANCE_METERS = 5
const REFERENCE_DIFF_CHUNK_METERS = 20

const isSameCoordinate = (a: TrackEditReferenceCoordinate, b: TrackEditReferenceCoordinate) =>
  a[0] === b[0] && a[1] === b[1]

const distSqToSegment = (
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
) => {
  const dx = bx - ax
  const dy = by - ay
  if (dx === 0 && dy === 0) {
    const rx = px - ax
    const ry = py - ay
    return rx * rx + ry * ry
  }
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / (dx * dx + dy * dy)))
  const cx = ax + t * dx
  const cy = ay + t * dy
  const ex = px - cx
  const ey = py - cy
  return ex * ex + ey * ey
}

const projectCoordinate = (
  coordinate: TrackEditReferenceCoordinate,
  metersPerDegreeLon: number,
): TrackEditReferenceCoordinate => [
  coordinate[0] * metersPerDegreeLon,
  coordinate[1] * METERS_PER_DEGREE_LAT,
]

const nearestDistanceSqToPolyline = (
  polyline: TrackEditReferenceCoordinate[],
  point: TrackEditReferenceCoordinate,
) => {
  if (polyline.length === 0) return Number.POSITIVE_INFINITY
  if (polyline.length === 1) {
    const dx = polyline[0][0] - point[0]
    const dy = polyline[0][1] - point[1]
    return dx * dx + dy * dy
  }

  let bestDistance = Number.POSITIVE_INFINITY
  for (let index = 1; index < polyline.length; index += 1) {
    const distanceSq = distSqToSegment(
      point[0],
      point[1],
      polyline[index - 1][0],
      polyline[index - 1][1],
      polyline[index][0],
      polyline[index][1],
    )
    if (distanceSq < bestDistance) {
      bestDistance = distanceSq
    }
  }

  return bestDistance
}

const interpolateCoordinate = (
  start: TrackEditReferenceCoordinate,
  end: TrackEditReferenceCoordinate,
  fraction: number,
): TrackEditReferenceCoordinate => [
  start[0] + (end[0] - start[0]) * fraction,
  start[1] + (end[1] - start[1]) * fraction,
]

const appendCoordinate = (
  segment: TrackEditReferenceCoordinate[],
  coordinate: TrackEditReferenceCoordinate,
) => {
  if (segment.length === 0 || !isSameCoordinate(segment[segment.length - 1], coordinate)) {
    segment.push(coordinate)
  }
}

export const buildTrackEditReferenceDiffSegments = (
  originalCoordinates: TrackEditReferenceCoordinate[],
  currentCoordinates: TrackEditReferenceCoordinate[],
): TrackEditReferenceDiffSegment[] => {
  if (originalCoordinates.length < 2) return []
  if (currentCoordinates.length < 2) {
    return [{ coordinates: originalCoordinates, startDistanceM: 0 }]
  }

  const allCoordinates = [...originalCoordinates, ...currentCoordinates]
  const averageLatitude =
    allCoordinates.reduce((sum, [, lat]) => sum + lat, 0) / Math.max(allCoordinates.length, 1)
  const metersPerDegreeLon = Math.cos((averageLatitude * Math.PI) / 180) * METERS_PER_DEGREE_LAT
  const projectedCurrentCoordinates = currentCoordinates.map((coordinate) =>
    projectCoordinate(coordinate, metersPerDegreeLon),
  )
  const toleranceSq = REFERENCE_DIFF_TOLERANCE_METERS ** 2
  const differingSegments: TrackEditReferenceDiffSegment[] = []
  let activeSegment: TrackEditReferenceCoordinate[] = []
  let activeSegmentStartDistanceM = 0
  let cumulativeDistanceM = 0

  for (let index = 1; index < originalCoordinates.length; index += 1) {
    const start = originalCoordinates[index - 1]
    const end = originalCoordinates[index]
    const projectedStart = projectCoordinate(start, metersPerDegreeLon)
    const projectedEnd = projectCoordinate(end, metersPerDegreeLon)
    const segmentLength = Math.hypot(
      projectedEnd[0] - projectedStart[0],
      projectedEnd[1] - projectedStart[1],
    )
    const chunkCount = Math.max(1, Math.ceil(segmentLength / REFERENCE_DIFF_CHUNK_METERS))

    for (let chunkIndex = 1; chunkIndex <= chunkCount; chunkIndex += 1) {
      const chunkStart = interpolateCoordinate(start, end, (chunkIndex - 1) / chunkCount)
      const chunkEnd = interpolateCoordinate(start, end, chunkIndex / chunkCount)
      const chunkMidpoint = interpolateCoordinate(start, end, (chunkIndex - 0.5) / chunkCount)
      const isDifferent =
        nearestDistanceSqToPolyline(
          projectedCurrentCoordinates,
          projectCoordinate(chunkMidpoint, metersPerDegreeLon),
        ) > toleranceSq

      if (isDifferent) {
        if (activeSegment.length === 0) {
          activeSegmentStartDistanceM =
            cumulativeDistanceM + (segmentLength * (chunkIndex - 1)) / chunkCount
        }
        appendCoordinate(activeSegment, chunkStart)
        appendCoordinate(activeSegment, chunkEnd)
        continue
      }

      if (activeSegment.length >= 2) {
        differingSegments.push({
          coordinates: activeSegment,
          startDistanceM: activeSegmentStartDistanceM,
        })
      }
      activeSegment = []
    }

    cumulativeDistanceM += segmentLength
  }

  if (activeSegment.length >= 2) {
    differingSegments.push({
      coordinates: activeSegment,
      startDistanceM: activeSegmentStartDistanceM,
    })
  }

  return differingSegments
}
